import { useEffect, useState } from "react";
import type { VliteInstance } from "../types";

export interface VlitePlaybackState {
  isPlaying: boolean;
  isEnded: boolean;
  isFullscreen: boolean;
}

export function useVlitePlaybackState(
  instance: VliteInstance | null
): VlitePlaybackState {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isEnded, setIsEnded] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (!instance) {
      return;
    }

    const container =
      (instance as { container?: EventTarget }).container ??
      (instance as { elements?: { container?: EventTarget } }).elements
        ?.container;
    if (!container) {
      return;
    }

    const handlePlay = () => {
      setIsPlaying(true);
      setIsEnded(false);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setIsEnded(true);
    };

    const handleEnterFullscreen = () => {
      setIsFullscreen(true);
    };

    const handleExitFullscreen = () => {
      setIsFullscreen(false);
    };

    container.addEventListener("play", handlePlay);
    container.addEventListener("pause", handlePause);
    container.addEventListener("ended", handleEnded);
    container.addEventListener("enterfullscreen", handleEnterFullscreen);
    container.addEventListener("exitfullscreen", handleExitFullscreen);

    return () => {
      container.removeEventListener("play", handlePlay);
      container.removeEventListener("pause", handlePause);
      container.removeEventListener("ended", handleEnded);
      container.removeEventListener("enterfullscreen", handleEnterFullscreen);
      container.removeEventListener("exitfullscreen", handleExitFullscreen);
    };
  }, [instance]);

  return {
    isPlaying,
    isEnded,
    isFullscreen,
  };
}
